import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { AskRequest, User } from '@hone-automation/common';
import { format } from 'date-fns';
import { useQueueContext } from '../../../Queue/QueueContext';
import { useAuth } from 'context/useAuthContext';
import { useQueryState } from 'hooks/useQueryState';
import { useGetAllUsersByLocation } from 'hooks';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { useBulkAddToGroup, useBulkRemoveFromGroup, useCreateGroup } from 'hooks/queue';
import { useGroups } from '../../../Dashboard/useGroups';
import { DraggableRequestCard, GroupCard, CollapsibleGroupCard } from './components/RequestCard';
import type { WorkState, RequestCardAssignee } from './components/RequestCard';
import { GroupHeader } from './components/GroupHeader';
import { BrowserToolbar } from './components/BrowserToolbar';
import { MultiSelectBar } from './components/BrowserToolbar/components/MultiSelectBar';
import type {
  AssigneeFilterOption,
  DueDatePreset,
  DateRange,
  RequestTypeFilter,
} from './components/BrowserToolbar/components/FilterModal';
import {
  getLastActivityDate,
  getDateForField,
  dateFieldLabels,
  DateGroupKey,
  parseLocalDate,
  type DateField,
} from './utils/dateGrouping';
import type { AssignedToType } from '../../types/Todos';
import { deriveUserType } from '../../Common';
import { EmptyState } from '../../Common/EmptyState';
import { getShortId } from '../../utils/shortId';
import { IconAddNew, IconCancel, IconFilter, IconTaskNew } from 'components/Icons';
import { formatTime } from '../../../Queue/RequestItem/utils/formatDateQ';
import { QueueCreateAskReq } from '../../../AskRequest';
import { CreateRequestEmailModal } from '../../../AskRequest/CreateRequestEmailModal';
import { CreateGroupModal } from '../../../AskRequest/CreateGroupModal';
import { useSelection, useToolbarFilters, useDateGroups } from './hooks';
import { ResolveDialog } from '../ConversationPanel/components/Header/components/ActionsDropdown/ResolveDialog';
import { ReopenDialog } from '../ConversationPanel/components/Header/components/ActionsDropdown/ReopenDialog';
import { SkeletonLoading } from './components/SkeletonLoading';
import './BrowserPanel.scss';

interface BrowserPanelProps {
  /**
   * Show blockers at top of Me/Team/Client lists (in addition to Blockers tab).
   * Disabled by default - enable when ready to test this feature.
   */
  showBlockersInLists?: boolean;
  /** Skip automatic first-item selection (useful for compact layouts). */
  disableAutoSelect?: boolean;
  /** Disable syncing selection to URL and reading from URL. */
  disableUrlSync?: boolean;
}

/**
 * BrowserPanel - Middle panel for browsing and selecting requests.
 * Displays a list of requests with search, filters, and sorting.
 *
 * Selection behavior:
 * - Click: Select single item, clear others
 * - Cmd/Ctrl+Click: Toggle item in selection
 * - Shift+Click: Range select from last clicked to current
 */
export const BrowserPanel = ({ showBlockersInLists = false, disableAutoSelect = false }: BrowserPanelProps) => {
  const { state, dispatch, queryData } = useQueueContext();
  const { activeView, activeGroupId, selectedItem } = state;
  const { queryRequests, isQueryLoading, queryView, viewCounts } = queryData;
  const { userId, user: authUser } = useAuth();
  const currentLocationId = useLocationsStore(state => state.currentLocationId);
  const { data: locationUsers } = useGetAllUsersByLocation(currentLocationId);
  const [requestIdFromUrl, setRequestIdUrl] = useQueryState('requestId');

  // Resolve location name from global locations store
  const allLocations = useLocationsStore(state => state.allLocations);
  const locationName = useMemo(() => {
    const matchedLocation = currentLocationId
      ? allLocations.find(loc => loc.id === currentLocationId || (loc as any).locationId === currentLocationId)
      : undefined;
    return (matchedLocation && ((matchedLocation as any).locationName || (matchedLocation as any).name)) || '';
  }, [currentLocationId, allLocations]);

  const requestCardUsers = useMemo<User[] | undefined>(() => {
    if (!locationUsers) return undefined;
    return (locationUsers as any[]).map(u => {
      const name = u.name || u.username || u.email?.split('@')?.[0] || u.userId;
      const username = u.username || u.email || u.userId;
      return {
        userId: u.userId,
        name,
        username,
        deleted: u.deleted,
      } as User;
    });
  }, [locationUsers]);

  // Current user for assign dropdown
  const currentUser = useMemo(() => {
    if (!authUser || !userId) return undefined;
    return { userId, name: authUser.displayName || authUser.email?.split('@')[0] || 'Me' };
  }, [authUser, userId]);

  // Patch mutation for bulk operations
  const patchMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      dispatch({
        type: 'SET_REQUESTS',
        payload: queryRequests.map(req => (req.id === data.id ? data : req)),
      });
    }
  });

  // Group operations
  const { addMultipleToGroup } = useBulkAddToGroup(currentLocationId || '');
  const { removeMultipleFromGroup } = useBulkRemoveFromGroup(currentLocationId || '');
  const { createGroup } = useCreateGroup(currentLocationId || '');

  // Use shared hook for groups (same source as Header/ActionsDropdown)
  const groups = useGroups();

  /**
   * Blockers that belong to the current view (Me/Team/Client)
   * These are displayed at the top of the list in a separate section.
   * When in Blockers view, blockers are shown in the main list (not separately).
   *
   * With server-side filtering, the server returns all requests for a view
   * including blockers. We extract them here to show in a separate section.
   */
  const viewBlockers = useMemo(() => {
    // Don't show separate blockers section in Blockers or Completed view
    if (activeView === 'Blockers' || activeView === 'Completed') return [];

    if (queryView !== activeView) return [];

    // Filter blockers from the server response
    return queryRequests.filter(req => req.priority === 'Blocker');
  }, [activeView, queryView, queryRequests]);

  /**
   * Server-filtered requests - excluding blockers when shown in separate section.
   * The server handles filtering by view (Blockers, Me, My Team, Client, Completed).
   */
  const viewFilteredRequests = useMemo(() => {
    // In Blockers view or Completed view, show all requests
    if (activeView === 'Blockers' || activeView === 'Completed') {
      return queryRequests;
    }
    // In other views (including Groups), exclude blockers (they're shown in the blockers section)
    return queryRequests.filter(req => req.priority !== 'Blocker');
  }, [activeView, queryRequests]);

  const rawViewCount = viewCounts[activeView || 'Blockers'];
  const activeViewCount = Array.isArray(rawViewCount) ? rawViewCount.length : rawViewCount || 0;

  // Multi-selection state (from useSelection hook)
  const {
    selectedIds,
    setSelectedIds,
    lastClickedId,
    setLastClickedId,
    isMultiSelectMode,
    selectAll: selectAllIds,
    clearSelection,
    handleCheckChange,
  } = useSelection();
  // Determine if all selected items are blockers
  const selectedAreBlockers = useMemo(
    () =>
      selectedIds.size > 0 &&
      queryRequests.filter(req => selectedIds.has(req.id)).every(req => req.priority === 'Blocker'),
    [selectedIds, queryRequests]
  );

  // Focus state (separate from selection - for keyboard navigation)
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [focusedGroupKey, setFocusedGroupKey] = useState<DateGroupKey | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isBulkResolveOpen, setIsBulkResolveOpen] = useState(false);
  const [isBulkReopenOpen, setIsBulkReopenOpen] = useState(false);
  const [bulkReopenInfo, setBulkReopenInfo] = useState<string | null>(null);

  // Sorting state: true = oldest first (default), false = newest first
  const [sortAscending, setSortAscending] = useState(true);

  // Date field for grouping (what date to use for groups)
  const [dateField, setDateField] = useState<DateField>('lastMessage');

  // Toolbar filters (extracted to hook)
  const {
    filters,
    setMentionFilterActive,
    setAttachmentFilterActive,
    setAssignedFilterActive,
    setGroupFilterActive,
    setWorkStateFilter,
    setDueDateFilter,
    setDateRangeFilter,
    setRequestTypeFilter,
    setAssigneeFilter,
    setSearchQuery,
    resetFilters,
    checkHasMention,
    checkHasAttachment,
    checkHasAssignee,
    checkGetWorkState,
    filterRequests,
    filterCounts,
    toolbarFilteredRequests,
    highlightQuery,
  } = useToolbarFilters({
    viewFilteredRequests,
    userId,
    dateField,
  });

  const {
    mentionFilterActive,
    attachmentFilterActive,
    assignedFilterActive,
    groupFilterActive,
    workStateFilter,
    dueDateFilter,
    dateRangeFilter,
    requestTypeFilter,
    assigneeFilter,
    searchQuery,
  } = filters;

  // All known groups from viewCounts (available across all views)
  const groupSummaries = useMemo(() => {
    return (viewCounts?.Groups ?? []) as { id: string; name: string; requestCount: number }[];
  }, [viewCounts]);

  // Filter out child requests from top-level rendering
  // Child requests (those with parentId) are rendered inside CollapsibleGroupCard
  // Exception: when viewing a specific group (activeView === 'Groups' with activeGroupId),
  // show child requests directly since we're viewing the group's contents
  const topLevelRequests = useMemo(() => {
    // When viewing a specific group's contents, show all requests (they're the children)
    if (activeView === 'Groups' && activeGroupId) {
      return toolbarFilteredRequests;
    }

    // Collect parentIds from current view requests to find which groups have children here
    const parentIdsInView = new Set<string>();
    viewFilteredRequests.forEach(r => {
      if (r.parentId) parentIdsInView.add(r.parentId);
    });

    // Get groups that are actual AskRequest objects in the response (type === 'Group')
    const groupRequestsInView = new Map<string, AskRequest>();
    viewFilteredRequests.forEach(r => {
      if (r.type === 'Group') groupRequestsInView.set(r.id, r);
    });

    // For groups that have children in this view but aren't in the response,
    // create synthetic AskRequest objects from viewCounts group summaries
    const syntheticGroups = new Map<string, AskRequest>();
    parentIdsInView.forEach(parentId => {
      if (!groupRequestsInView.has(parentId)) {
        const summary = groupSummaries.find(g => g.id === parentId);
        if (summary) {
          syntheticGroups.set(parentId, {
            id: summary.id,
            title: summary.name,
            type: 'Group',
            status: 'Open',
            priority: 'Normal',
            locationId: currentLocationId || '',
            createdAt: new Date(),
            updatedAt: new Date(),
            deleted: false,
            recentlyResolved: false,
            canReopen: false,
            canRecategorize: false,
          } as AskRequest);
        }
      }
    });

    // Merge: real group requests + synthetic groups
    const allGroupsMap = new Map([...groupRequestsInView, ...syntheticGroups]);

    // Determine which groups should be visible — only if they have children in the current view
    const visibleGroupIds = new Set<string>();
    allGroupsMap.forEach((_group, groupId) => {
      const children = viewFilteredRequests.filter(r => r.parentId === groupId);
      const hasMatchingChild = children.some(child => toolbarFilteredRequests.some(r => r.id === child.id));
      if (hasMatchingChild) {
        visibleGroupIds.add(groupId);
      }
    });

    // Build top-level list:
    // - Non-child, non-group requests that pass toolbar filters
    // - Visible groups (real or synthetic)
    const result: AskRequest[] = [];

    toolbarFilteredRequests.forEach(r => {
      if (!r.parentId && r.type !== 'Group') {
        result.push(r);
      }
    });

    allGroupsMap.forEach((group, groupId) => {
      if (visibleGroupIds.has(groupId)) {
        result.push(group);
      }
    });

    return result;
  }, [toolbarFilteredRequests, viewFilteredRequests, activeView, activeGroupId, groupSummaries, currentLocationId]);

  // Create request modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [includeChildren, setIncludeChildren] = useState(false);

  // Blockers section collapsed state (separate from date groups)
  const [blockersCollapsed, setBlockersCollapsed] = useState(false);

  // Date groups and collapsed state (extracted to hook)
  const {
    dateGroups,
    collapsedGroups,
    setCollapsedGroups,
    toggleGroup,
    toggleAllGroups,
    resetCollapsedGroups,
    expandGroup,
    visibleRequests,
    findGroupForRequest,
    hasInitialized,
  } = useDateGroups({
    filteredRequests: topLevelRequests,
    sortAscending,
    dateField,
  });

  // Apply toolbar filters to viewBlockers (same filters as main list)
  const filteredViewBlockers = useMemo(() => filterRequests(viewBlockers), [filterRequests, viewBlockers]);

  // Combined visible requests: blockers (if expanded) + regular visible requests
  // Used for keyboard navigation and selection
  const allVisibleRequests = useMemo(() => {
    const blockersToShow = blockersCollapsed ? [] : filteredViewBlockers;
    return [...blockersToShow, ...visibleRequests];
  }, [blockersCollapsed, filteredViewBlockers, visibleRequests]);

  // Toggle blockers section collapsed state
  const toggleBlockersSection = useCallback(() => {
    setBlockersCollapsed(prev => !prev);
  }, []);

  // Build assignee options from location users
  const assigneeOptions: AssigneeFilterOption[] = useMemo(() => {
    if (!locationUsers) return [];
    return (locationUsers as any[])
      .map(user => ({
        userId: user.userId,
        name: user.name || user.username || user.email?.split('@')[0] || 'Unknown',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [locationUsers]);

  // Update visible request IDs in context whenever toolbar filtered requests change
  // This is used by Dashboard to determine if selectedItem should be shown
  // Include both regular requests and filtered view blockers
  const lastVisibleIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const ids = new Set([
      ...toolbarFilteredRequests.map((r: AskRequest) => r.id),
      ...filteredViewBlockers.map((r: AskRequest) => r.id),
      ...topLevelRequests.map((r: AskRequest) => r.id),
    ]);

    // Simple equality check to prevent infinite loops
    const lastIds = lastVisibleIdsRef.current;
    if (ids.size === lastIds.size) {
      let isSame = true;
      for (const id of ids) {
        if (!lastIds.has(id)) {
          isSame = false;
          break;
        }
      }
      if (isSame) return;
    }

    lastVisibleIdsRef.current = ids;
    dispatch({ type: 'SET_VISIBLE_REQUEST_IDS', payload: ids });
  }, [toolbarFilteredRequests, filteredViewBlockers, topLevelRequests, dispatch]);

  // Reset collapsed groups when toolbar filters, search, or dateField change
  const prevToolbarFilters = useRef({
    mentionFilterActive,
    attachmentFilterActive,
    assignedFilterActive,
    workStateFilterSize: workStateFilter.size,
    dueDateFilter,
    dateRangeFrom: dateRangeFilter.from,
    dateRangeTo: dateRangeFilter.to,
    requestTypeFilterSize: requestTypeFilter.size,
    assigneeFilter,
    searchQuery,
    dateField,
  });
  useEffect(() => {
    const prev = prevToolbarFilters.current;
    const hasFilterChanged =
      prev.mentionFilterActive !== mentionFilterActive ||
      prev.attachmentFilterActive !== attachmentFilterActive ||
      prev.assignedFilterActive !== assignedFilterActive ||
      prev.workStateFilterSize !== workStateFilter.size ||
      prev.dueDateFilter !== dueDateFilter ||
      prev.dateRangeFrom !== dateRangeFilter.from ||
      prev.dateRangeTo !== dateRangeFilter.to ||
      prev.requestTypeFilterSize !== requestTypeFilter.size ||
      prev.assigneeFilter !== assigneeFilter ||
      prev.searchQuery !== searchQuery ||
      prev.dateField !== dateField;

    if (hasFilterChanged && hasInitialized) {
      // Recompute collapsed groups when toolbar filters or dateField change
      resetCollapsedGroups();
    }

    prevToolbarFilters.current = {
      mentionFilterActive,
      attachmentFilterActive,
      assignedFilterActive,
      workStateFilterSize: workStateFilter.size,
      dueDateFilter,
      dateRangeFrom: dateRangeFilter.from,
      dateRangeTo: dateRangeFilter.to,
      requestTypeFilterSize: requestTypeFilter.size,
      assigneeFilter,
      searchQuery,
      dateField,
    };
  }, [
    mentionFilterActive,
    attachmentFilterActive,
    assignedFilterActive,
    workStateFilter.size,
    dueDateFilter,
    dateRangeFilter.from,
    dateRangeFilter.to,
    requestTypeFilter.size,
    assigneeFilter,
    searchQuery,
    dateField,
    hasInitialized,
    resetCollapsedGroups,
  ]);

  // Helper to expand group containing a request and scroll to it
  const ensureRequestVisible = useCallback(
    (requestId: string) => {
      // Find which group contains the request
      const groupKey = findGroupForRequest(requestId);
      if (!groupKey) return false;

      // Expand the group if collapsed
      expandGroup(groupKey);

      // Scroll to the item after DOM update, centered vertically
      requestAnimationFrame(() => {
        const element = listRef.current?.querySelector(`[data-request-id="${requestId}"]`);
        element?.scrollIntoView({ block: 'center' });
      });

      return true;
    },
    [findGroupForRequest, expandGroup]
  );

  // Keep selected item visible when dateField or sort order changes
  const prevDateField = useRef(dateField);
  const prevSortAscending = useRef(sortAscending);
  useEffect(() => {
    const dateFieldChanged = prevDateField.current !== dateField;
    const sortChanged = prevSortAscending.current !== sortAscending;

    if ((dateFieldChanged || sortChanged) && focusedId) {
      ensureRequestVisible(focusedId);
    }

    prevDateField.current = dateField;
    prevSortAscending.current = sortAscending;
  }, [dateField, sortAscending, focusedId, ensureRequestVisible]);

  // On initial load, scroll to requestId from URL
  const hasScrolledToUrlRequest = useRef(false);
  useEffect(() => {
    // Ensure requestIdFromUrl is a string
    const urlRequestId = typeof requestIdFromUrl === 'string' ? requestIdFromUrl : null;

    if (!hasScrolledToUrlRequest.current && urlRequestId && dateGroups.length > 0 && hasInitialized) {
      // Delay to ensure collapsed groups initialization has settled
      const timeoutId = setTimeout(() => {
        const request = viewFilteredRequests.find((r: AskRequest) => r.id === urlRequestId);
        if (request && !hasScrolledToUrlRequest.current) {
          hasScrolledToUrlRequest.current = true;
          setFocusedId(urlRequestId);
          setSelectedIds(new Set([urlRequestId]));
          setLastClickedId(urlRequestId);
          dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
          ensureRequestVisible(urlRequestId);
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [requestIdFromUrl, dateGroups, viewFilteredRequests, ensureRequestVisible, dispatch, hasInitialized]);

  // Sync focused item with context's selectedItem
  useEffect(() => {
    if (!selectedItem) return;

    // If focusedId is empty OR focusedId is no longer in the current view, sync to selectedItem
    const focusedStillVisible = focusedId && viewFilteredRequests.some(r => r.id === focusedId);
    if (!focusedStillVisible && selectedItem.id !== focusedId) {
      setFocusedId(selectedItem.id);
      if (selectedItem.type !== 'Group') {
        setSelectedIds(new Set([selectedItem.id]));
        setLastClickedId(selectedItem.id);
      }
    }
  }, [selectedItem, focusedId, viewFilteredRequests]);

  // ============================================================================
  // SELECTION LOGIC - Simplified architecture:
  // - QueueContext handles deep link selection (URL with requestId)
  // - BrowserPanel auto-selects first item when view changes (no requestId)
  // - Local focus/selection state syncs with context's selectedItem
  // ============================================================================

  // Track whether we have a URL requestId (deep link) - QueueContext handles this
  const hasUrlRequestId = typeof requestIdFromUrl === 'string' && requestIdFromUrl.trim() !== '';

  // Refs for stable access to current values without triggering effect re-runs
  const selectedItemRef = useRef(selectedItem);
  const viewFilteredRequestsRef = useRef(viewFilteredRequests);
  const focusedIdRef = useRef(focusedId);
  selectedItemRef.current = selectedItem;
  viewFilteredRequestsRef.current = viewFilteredRequests;
  focusedIdRef.current = focusedId;

  // Track previous activeView/activeGroupId to detect changes
  const prevActiveViewRef = useRef(activeView);
  const prevActiveGroupIdRef = useRef(activeGroupId);
  const isFirstMount = useRef(true);
  // Flag set by Effect 1 so Effect 2 knows not to re-populate selectedIds after a view change
  const viewJustChangedRef = useRef(false);

  // Effect 1: Clear selection + reset toolbar filters when view/group changes
  useEffect(() => {
    // Always clear multi-selection when view or group changes
    clearSelection();
    viewJustChangedRef.current = true;

    // Skip toolbar filter reset on first mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevActiveViewRef.current = activeView;
      prevActiveGroupIdRef.current = activeGroupId;
      return;
    }

    // Only reset toolbar filters if view or group actually changed
    if (prevActiveViewRef.current === activeView && prevActiveGroupIdRef.current === activeGroupId) {
      return;
    }
    prevActiveViewRef.current = activeView;
    prevActiveGroupIdRef.current = activeGroupId;

    setMentionFilterActive(false);
    setAttachmentFilterActive(false);
    setAssignedFilterActive(false);
    setGroupFilterActive(false);
    setWorkStateFilter(new Set());
    setAssigneeFilter(null);
    resetCollapsedGroups();
  }, [activeView, activeGroupId]); // Note: resetCollapsedGroups intentionally excluded to avoid loops

  // Effect 2: Auto-select first request when data is ready
  // Runs when: loading completes, dateGroups change, or view changes
  useEffect(() => {
    // Skip if still loading
    if (isQueryLoading) return;

    // If the view just changed, keep selectedIds empty (Effect 1 already cleared it)
    const skipSelection = viewJustChangedRef.current;
    viewJustChangedRef.current = false;

    // Skip if dateGroups not ready yet (data loaded but not grouped)
    const currentRequests = viewFilteredRequestsRef.current;
    if (dateGroups.length === 0 && currentRequests.length > 0) return;

    const currentSelectedItem = selectedItemRef.current;
    const currentFocusedId = focusedIdRef.current;

    // If there's a URL requestId, QueueContext handles selection
    // Just sync local focus state when selectedItem is set
    if (hasUrlRequestId && currentSelectedItem) {
      if (currentFocusedId !== currentSelectedItem.id) {
        setFocusedId(currentSelectedItem.id);
        if (currentSelectedItem.type !== 'Group' && !skipSelection) {
          setSelectedIds(new Set([currentSelectedItem.id]));
          setLastClickedId(currentSelectedItem.id);
        }
        requestAnimationFrame(() => {
          ensureRequestVisible(currentSelectedItem.id);
        });
      }
      return;
    }

    // In Groups view: the group parent is shown in the conversation panel (via navigateToGroup
    // or deep link). Never auto-select a child request — let the user choose manually.
    // This also covers the transient window where selectedItem is null while the group
    // parent is being fetched by navigateToGroup.
    if (activeView === 'Groups') return;

    // If selectedItem exists in current view, sync local state
    // Don't carry over a group selection into non-Groups views
    const isStaleGroupSelection = currentSelectedItem?.type === 'Group' && activeView !== 'Groups';
    if (currentSelectedItem && !isStaleGroupSelection && currentRequests.some(r => r.id === currentSelectedItem.id)) {
      if (currentFocusedId !== currentSelectedItem.id) {
        setFocusedId(currentSelectedItem.id);
        if (!skipSelection) {
          setSelectedIds(new Set([currentSelectedItem.id]));
          setLastClickedId(currentSelectedItem.id);
        }
      }
      return;
    }

    // Auto-select first request in the sorted list (unless disabled)
    const firstRequest = dateGroups[0]?.requests[0];
    if (firstRequest && !disableAutoSelect) {
      setFocusedId(firstRequest.id);
      if (!skipSelection) {
        setSelectedIds(new Set([firstRequest.id]));
        setLastClickedId(firstRequest.id);
      }
      dispatch({ type: 'SET_SELECTED_ITEM', payload: firstRequest });
      // In Groups view the URL already carries parentId — don't set requestId on auto-select
      // so that a refresh just reopens the group without preselecting any specific request.
      if (activeView !== 'Groups') {
        setRequestIdUrl(firstRequest.id);
      }

      requestAnimationFrame(() => {
        ensureRequestVisible(firstRequest.id);
      });
    } else if (!currentSelectedItem) {
      // No requests in view - clear local selection state
      if (currentFocusedId) {
        setFocusedId(null);
        setSelectedIds(new Set());
        setLastClickedId(null);
      }
      // Only clear URL if there's actually a requestId to remove
      // (avoids triggering navigateTo which changes location → new setRequestIdUrl ref → re-runs this effect)
      if (hasUrlRequestId) {
        dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
        setRequestIdUrl(undefined);
      }
    }
  }, [
    activeView,
    dateGroups,
    isQueryLoading,
    hasUrlRequestId,
    disableAutoSelect,
    dispatch,
    setRequestIdUrl,
    ensureRequestVisible,
  ]);

  // Toggle sort order
  const toggleSortOrder = useCallback(() => {
    setSortAscending(prev => !prev);
  }, []);

  /**
   * Handle click on a request card
   * - Normal click: Select single item
   * - Cmd/Ctrl+Click: Toggle in selection
   * - Shift+Click: Range select
   */
  const handleCardClick = useCallback(
    (requestId: string, e: React.MouseEvent) => {
      // Look in viewFilteredRequests, viewBlockers, and topLevelRequests (for synthetic groups)
      const request =
        viewFilteredRequests.find((r: AskRequest) => r.id === requestId) ||
        viewBlockers.find((r: AskRequest) => r.id === requestId) ||
        topLevelRequests.find((r: AskRequest) => r.id === requestId);
      if (!request) return;

      // Groups don't participate in multi-select — clear any existing selection
      if (request.type === 'Group') {
        setSelectedIds(new Set());
        setLastClickedId(null);
        setFocusedId(requestId);
        dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
        // Don't set requestId for groups — parentId in URL already identifies the group context
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;
      const isShift = e.shiftKey;

      if (isShift && lastClickedId) {
        // Shift+Click: Range select
        const ids = allVisibleRequests.map((r: AskRequest) => r.id);
        const startIdx = ids.indexOf(lastClickedId);
        const endIdx = ids.indexOf(requestId);

        if (startIdx !== -1 && endIdx !== -1) {
          const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          // Exclude groups from range selection
          const rangeIds = allVisibleRequests
            .slice(from, to + 1)
            .filter((r: AskRequest) => r.type !== 'Group')
            .map((r: AskRequest) => r.id);

          setSelectedIds(prev => {
            const newSet = new Set(prev);
            rangeIds.forEach(id => newSet.add(id));
            return newSet;
          });
        }
      } else if (isMeta) {
        // Cmd/Ctrl+Click: Toggle selection
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          if (newSet.has(requestId)) {
            newSet.delete(requestId);
          } else {
            newSet.add(requestId);
          }
          return newSet;
        });
        setLastClickedId(requestId);
      } else {
        // Normal click: Single select
        setSelectedIds(new Set([requestId]));
        setLastClickedId(requestId);
      }

      // Always update focused item and context
      setFocusedId(requestId);
      dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
      setRequestIdUrl(requestId);
    },
    [viewFilteredRequests, viewBlockers, topLevelRequests, allVisibleRequests, lastClickedId, dispatch, setRequestIdUrl]
  );

  /**
   * Scroll focused item into view
   */
  const scrollToFocused = useCallback((id: string) => {
    const element = listRef.current?.querySelector(`[data-request-id="${id}"]`);
    element?.scrollIntoView({ block: 'center' });
  }, []);

  /**
   * Move focus by delta (positive = down, negative = up)
   * Uses visibleRequests to skip collapsed groups
   */
  const moveFocus = useCallback(
    (delta: number, extendSelection = false) => {
      if (visibleRequests.length === 0) return;

      const ids = visibleRequests.map((r: AskRequest) => r.id);
      const currentIdx = focusedId ? ids.indexOf(focusedId) : -1;
      let newIdx: number;

      if (currentIdx === -1) {
        newIdx = delta > 0 ? 0 : ids.length - 1;
      } else {
        newIdx = Math.max(0, Math.min(ids.length - 1, currentIdx + delta));
      }

      const newId = ids[newIdx];
      const request = visibleRequests[newIdx];

      setFocusedId(newId);
      scrollToFocused(newId);

      if (extendSelection && lastClickedId) {
        // Shift+Arrow: Extend selection
        const startIdx = ids.indexOf(lastClickedId);
        const [from, to] = startIdx < newIdx ? [startIdx, newIdx] : [newIdx, startIdx];
        const rangeIds = ids.slice(from, to + 1);
        setSelectedIds(new Set(rangeIds));
      } else if (!extendSelection) {
        // Normal arrow: Single select
        setSelectedIds(new Set([newId]));
        setLastClickedId(newId);
      }

      dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
      setRequestIdUrl(newId);
    },
    [visibleRequests, focusedId, lastClickedId, scrollToFocused, dispatch, setRequestIdUrl]
  );

  /**
   * Jump to first or last item
   * Uses visibleRequests to skip collapsed groups
   */
  const jumpToEdge = useCallback(
    (toEnd: boolean, extendSelection = false) => {
      if (visibleRequests.length === 0) return;

      const ids = visibleRequests.map((r: AskRequest) => r.id);
      const newIdx = toEnd ? ids.length - 1 : 0;
      const newId = ids[newIdx];
      const request = visibleRequests[newIdx];

      setFocusedId(newId);
      scrollToFocused(newId);

      if (extendSelection && lastClickedId) {
        const startIdx = ids.indexOf(lastClickedId);
        const [from, to] = startIdx < newIdx ? [startIdx, newIdx] : [newIdx, startIdx];
        const rangeIds = ids.slice(from, to + 1);
        setSelectedIds(new Set(rangeIds));
      } else if (!extendSelection) {
        setSelectedIds(new Set([newId]));
        setLastClickedId(newId);
      }

      dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
      setRequestIdUrl(newId);
    },
    [visibleRequests, lastClickedId, scrollToFocused, dispatch, setRequestIdUrl]
  );

  /**
   * Select all visible items (Cmd/Ctrl+A)
   */
  const selectAll = useCallback(() => {
    const allIds = allVisibleRequests.filter((r: AskRequest) => r.type !== 'Group').map((r: AskRequest) => r.id);
    selectAllIds(allIds);
  }, [allVisibleRequests, selectAllIds]);

  /**
   * Deselect all items (alias for clearSelection from hook)
   */
  const deselectAll = clearSelection;

  /**
   * Select first remaining item after bulk operation
   * Called after items may have moved out of the current view
   */
  const selectFirstRemainingItem = useCallback(
    (excludeIds: Set<string>) => {
      // Find first visible request not in the excluded set
      const remaining = allVisibleRequests.find((r: AskRequest) => !excludeIds.has(r.id));
      if (remaining) {
        setFocusedId(remaining.id);
        setSelectedIds(new Set([remaining.id]));
        setLastClickedId(remaining.id);
        dispatch({ type: 'SET_SELECTED_ITEM', payload: remaining });
        setRequestIdUrl(remaining.id);
      } else {
        // No items remaining, clear selection
        deselectAll();
        dispatch({ type: 'SET_SELECTED_ITEM', payload: null });
        setRequestIdUrl(undefined);
      }
    },
    [allVisibleRequests, dispatch, setRequestIdUrl, deselectAll]
  );

  /**
   * Bulk action: Assign selected items to a user
   */
  const handleBulkAssign = useCallback(
    async (assigneeId: string | null) => {
      const ids = Array.from(selectedIds);
      const idsSet = new Set(ids);
      // Process sequentially to avoid race conditions
      for (const id of ids) {
        await patchMutation.mutateAsync({ id, assignee: assigneeId, locationId: currentLocationId });
      }
      selectFirstRemainingItem(idsSet);
    },
    [selectedIds, patchMutation, selectFirstRemainingItem, currentLocationId]
  );

  /**
   * Bulk action: Set due date for selected items
   */
  const handleBulkSetDueDate = useCallback(
    async (dueDate: Date | null) => {
      const ids = Array.from(selectedIds);
      // Format as YYYY-MM-DD for API (same as DueDateButton)
      const dueDateString = dueDate ? format(dueDate, 'yyyy-MM-dd') : null;
      // Process sequentially to avoid race conditions
      for (const id of ids) {
        await patchMutation.mutateAsync({ id, dueDate: dueDateString, locationId: currentLocationId });
      }
      // Due date doesn't move items, just deselect
      deselectAll();
    },
    [selectedIds, patchMutation, deselectAll, currentLocationId]
  );

  /**
   * Bulk action: Resolve selected items (set status to Closed)
   */
  const handleBulkResolve = useCallback(() => {
    if (selectedIds.size === 0) return;
    setIsBulkResolveOpen(true);
  }, [selectedIds.size]);

  const handleBulkResolveSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      const ids = Array.from(selectedIds);
      const idsSet = new Set(ids);
      // Process sequentially to avoid race conditions
      for (const id of ids) {
        await patchMutation.mutateAsync({ id, status: 'Closed', comment, locationId: currentLocationId });
      }
      setIsBulkResolveOpen(false);
      selectFirstRemainingItem(idsSet);
    },
    [selectedIds, patchMutation, selectFirstRemainingItem]
  );

  /**
   * Bulk action: Reopen selected items (set status to Open)
   */
  const handleBulkReopen = useCallback(() => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const reopenableCount = ids.reduce((count, id) => {
      const request = queryRequests.find(req => req.id === id);
      return request?.canReopen ? count + 1 : count;
    }, 0);

    if (reopenableCount !== ids.length) {
      setBulkReopenInfo(`${reopenableCount} of ${ids.length} selected can be reopened. Others will be skipped.`);
    } else {
      setBulkReopenInfo(null);
    }
    setIsBulkReopenOpen(true);
  }, [selectedIds, queryRequests]);

  const handleBulkReopenSubmit = useCallback(
    async ({ comment }: { comment: string }) => {
      const ids = Array.from(selectedIds);
      const reopenableIds = ids.filter(id => {
        const request = queryRequests.find(req => req.id === id);
        return request?.canReopen;
      });

      if (reopenableIds.length === 0) {
        setIsBulkReopenOpen(false);
        return;
      }

      const idsSet = new Set(reopenableIds);
      // Process sequentially to avoid race conditions
      for (const id of reopenableIds) {
        await patchMutation.mutateAsync({ id, status: 'Open', comment, locationId: currentLocationId });
      }
      setIsBulkReopenOpen(false);
      selectFirstRemainingItem(idsSet);
    },
    [selectedIds, queryRequests, patchMutation, selectFirstRemainingItem]
  );

  /**
   * Bulk action: Toggle blocker priority for selected items
   */
  const handleBulkToggleBlocker = useCallback(
    async (isBlocker: boolean) => {
      const ids = Array.from(selectedIds);
      const idsSet = new Set(ids);
      const priority = isBlocker ? 'Blocker' : 'Normal';
      // Process sequentially to avoid race conditions
      for (const id of ids) {
        await patchMutation.mutateAsync({ id, priority, locationId: currentLocationId });
      }
      selectFirstRemainingItem(idsSet);
    },
    [selectedIds, patchMutation, selectFirstRemainingItem, currentLocationId]
  );

  /**
   * Bulk action: Remove blocker priority for selected items
   */
  const handleRemoveToggleBlock = useCallback(async () => {
    const ids = Array.from(selectedIds);
    const idsSet = new Set(ids);
    for (const id of ids) {
      await patchMutation.mutateAsync({ id, priority: 'Normal', locationId: currentLocationId });
    }
    selectFirstRemainingItem(idsSet);
  }, [selectedIds, patchMutation, selectFirstRemainingItem, currentLocationId]);

  /**
   * Expand a group and focus its first item
   */
  const expandGroupAndFocusFirst = useCallback(
    (groupKey: DateGroupKey) => {
      const group = dateGroups.find(g => g.key === groupKey);
      if (!group || group.requests.length === 0) return;

      // Expand the group
      setCollapsedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupKey);
        return newSet;
      });

      // Focus first item in the group
      const firstRequest = group.requests[0];
      setFocusedId(firstRequest.id);
      setSelectedIds(new Set([firstRequest.id]));
      setLastClickedId(firstRequest.id);
      dispatch({ type: 'SET_SELECTED_ITEM', payload: firstRequest });
      setRequestIdUrl(firstRequest.id);

      // Scroll after state update
      setTimeout(() => scrollToFocused(firstRequest.id), 0);
    },
    [dateGroups, scrollToFocused, dispatch, setRequestIdUrl]
  );

  /**
   * Scroll to a group header
   */
  const scrollToGroup = useCallback((groupKey: DateGroupKey) => {
    const element = listRef.current?.querySelector(`[data-group-key="${groupKey}"]`);
    element?.scrollIntoView({ block: 'nearest' });
  }, []);

  /**
   * Focus a group header (clear item focus)
   */
  const focusGroup = useCallback(
    (groupKey: DateGroupKey) => {
      setFocusedGroupKey(groupKey);
      setFocusedId(null);
      scrollToGroup(groupKey);
    },
    [scrollToGroup]
  );

  /**
   * Focus an item (clear group focus)
   */
  const focusItem = useCallback(
    (request: AskRequest) => {
      setFocusedId(request.id);
      setFocusedGroupKey(null);
      setSelectedIds(new Set([request.id]));
      setLastClickedId(request.id);
      dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
      setRequestIdUrl(request.id);
      scrollToFocused(request.id);
    },
    [scrollToFocused, dispatch, setRequestIdUrl]
  );

  /**
   * Handle Left arrow:
   * - On item: collapse group, focus group header
   * - On group header: collapse if expanded
   */
  const handleLeftArrow = useCallback(() => {
    if (focusedGroupKey) {
      // On group header: collapse if expanded
      if (!collapsedGroups.has(focusedGroupKey)) {
        setCollapsedGroups(prev => new Set([...prev, focusedGroupKey]));
      }
      return;
    }

    if (focusedId) {
      // On item: collapse group and focus group header
      const groupKey = findGroupForRequest(focusedId);
      if (groupKey) {
        setCollapsedGroups(prev => new Set([...prev, groupKey]));
        focusGroup(groupKey);
      }
    }
  }, [focusedId, focusedGroupKey, findGroupForRequest, collapsedGroups, focusGroup]);

  /**
   * Handle Right arrow:
   * - On collapsed group header: expand and focus first item
   * - On expanded group header: focus first item
   * - On item: do nothing
   */
  const handleRightArrow = useCallback(() => {
    if (focusedGroupKey) {
      const group = dateGroups.find(g => g.key === focusedGroupKey);
      if (!group || group.requests.length === 0) return;

      // Expand if collapsed
      if (collapsedGroups.has(focusedGroupKey)) {
        setCollapsedGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(focusedGroupKey);
          return newSet;
        });
      }

      // Focus first item
      setTimeout(() => focusItem(group.requests[0]), 0);
    }
    // On item: do nothing
  }, [focusedGroupKey, dateGroups, collapsedGroups, focusItem]);

  /**
   * Handle Up/Down arrows with group header navigation
   */
  const handleVerticalArrow = useCallback(
    (delta: number, extendSelection = false) => {
      // Currently on a group header
      if (focusedGroupKey) {
        const currentGroupIdx = dateGroups.findIndex(g => g.key === focusedGroupKey);

        if (delta > 0) {
          // Down: if expanded, enter group; else go to next group header
          if (!collapsedGroups.has(focusedGroupKey)) {
            const group = dateGroups[currentGroupIdx];
            if (group.requests.length > 0) {
              focusItem(group.requests[0]);
              return;
            }
          }
          // Go to next group header
          if (currentGroupIdx < dateGroups.length - 1) {
            focusGroup(dateGroups[currentGroupIdx + 1].key);
          }
        } else {
          // Up: go to previous group's last item (if expanded) or header
          if (currentGroupIdx > 0) {
            const prevGroup = dateGroups[currentGroupIdx - 1];
            if (!collapsedGroups.has(prevGroup.key) && prevGroup.requests.length > 0) {
              focusItem(prevGroup.requests[prevGroup.requests.length - 1]);
            } else {
              focusGroup(prevGroup.key);
            }
          }
        }
        return;
      }

      // Currently on an item
      if (focusedId) {
        const ids = visibleRequests.map((r: AskRequest) => r.id);
        const currentIdx = ids.indexOf(focusedId);

        if (currentIdx === -1) {
          // Focus lost, go to first group
          if (dateGroups.length > 0) {
            focusGroup(dateGroups[0].key);
          }
          return;
        }

        const newIdx = currentIdx + delta;

        // Moving up from first item in a group
        if (delta < 0 && currentIdx === 0) {
          // Go to first group header
          focusGroup(dateGroups[0].key);
          return;
        }

        // Check if we're at the first item of a group (not the first group)
        if (delta < 0) {
          const currentGroupKey = findGroupForRequest(focusedId);
          const currentGroup = dateGroups.find(g => g.key === currentGroupKey);
          if (currentGroup && currentGroup.requests[0]?.id === focusedId) {
            // At first item of group, go to group header
            focusGroup(currentGroupKey!);
            return;
          }
        }

        // Moving down from last item - check for next collapsed group
        if (delta > 0 && newIdx >= ids.length) {
          const currentGroupKey = findGroupForRequest(focusedId);
          const currentGroupIdx = dateGroups.findIndex(g => g.key === currentGroupKey);
          if (currentGroupIdx < dateGroups.length - 1) {
            focusGroup(dateGroups[currentGroupIdx + 1].key);
          }
          return;
        }

        // Normal item-to-item movement
        if (newIdx >= 0 && newIdx < ids.length) {
          const request = visibleRequests[newIdx];

          if (extendSelection && lastClickedId) {
            const startIdx = ids.indexOf(lastClickedId);
            const [from, to] = startIdx < newIdx ? [startIdx, newIdx] : [newIdx, startIdx];
            setSelectedIds(new Set(ids.slice(from, to + 1)));
            setFocusedId(request.id);
            setFocusedGroupKey(null);
            dispatch({ type: 'SET_SELECTED_ITEM', payload: request });
            setRequestIdUrl(request.id);
            scrollToFocused(request.id);
          } else {
            focusItem(request);
          }
        }
      } else {
        // No focus, start at first group
        if (dateGroups.length > 0) {
          focusGroup(dateGroups[0].key);
        }
      }
    },
    [
      focusedId,
      focusedGroupKey,
      dateGroups,
      collapsedGroups,
      visibleRequests,
      findGroupForRequest,
      focusGroup,
      focusItem,
      lastClickedId,
      scrollToFocused,
      dispatch,
      setRequestIdUrl,
    ]
  );

  /**
   * Handle Enter/Space on group header: toggle expand/collapse
   */
  const handleEnterOnGroup = useCallback(() => {
    if (focusedGroupKey) {
      toggleGroup(focusedGroupKey);
    }
  }, [focusedGroupKey, toggleGroup]);

  /**
   * Handle keyboard navigation on the list
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isShift = e.shiftKey;
      const isMeta = e.metaKey || e.ctrlKey;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          handleVerticalArrow(1, isShift);
          break;
        case 'ArrowUp':
          e.preventDefault();
          handleVerticalArrow(-1, isShift);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handleLeftArrow();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleRightArrow();
          break;
        case 'Enter':
        case ' ':
          if (focusedGroupKey) {
            e.preventDefault();
            handleEnterOnGroup();
          }
          break;
        case 'Home':
          e.preventDefault();
          if (dateGroups.length > 0) {
            focusGroup(dateGroups[0].key);
          }
          break;
        case 'End':
          e.preventDefault();
          if (dateGroups.length > 0) {
            const lastGroup = dateGroups[dateGroups.length - 1];
            if (!collapsedGroups.has(lastGroup.key) && lastGroup.requests.length > 0) {
              focusItem(lastGroup.requests[lastGroup.requests.length - 1]);
            } else {
              focusGroup(lastGroup.key);
            }
          }
          break;
        case 'a':
          if (isMeta) {
            e.preventDefault();
            selectAll();
          }
          break;
        case 'Escape':
          // Clear multi-selection, keep single focused item
          if (focusedId) {
            setSelectedIds(new Set([focusedId]));
            setLastClickedId(focusedId);
          }
          break;
      }
    },
    [
      handleVerticalArrow,
      handleLeftArrow,
      handleRightArrow,
      handleEnterOnGroup,
      focusedGroupKey,
      focusedId,
      dateGroups,
      collapsedGroups,
      focusGroup,
      focusItem,
      selectAll,
    ]
  );

  const formatFullDateTime = (date: Date): string => {
    return format(date, 'PP p');
  };

  const formatCardDateLabel = (date: Date, groupKey: DateGroupKey): string => {
    if (groupKey === 'today' || groupKey === 'yesterday') {
      return formatTime(date);
    }

    if (groupKey === 'thisWeek' || groupKey === 'lastWeek') {
      return `${format(date, 'EEE')}, ${formatTime(date)}`;
    }

    // For month/year buckets, keep it compact. The group header provides the broader context.
    return format(date, 'MMM d');
  };

  /** Get assignee info - uses request.assignee if set, falls back to last comment's user */
  const getAssignee = (
    request: AskRequest
  ): { name?: string; type: AssignedToType; isExplicit: boolean } | undefined => {
    // First, check if request has an explicit assignee
    if (request.assignee && locationUsers) {
      // Cast to any since runtime data has name/username fields not in UserLocation type
      const assignedUser = locationUsers.find((u: any) => u.userId === request.assignee) as any;
      if (assignedUser) {
        const name = assignedUser.name || assignedUser.username || assignedUser.email;
        const type = deriveUserType({ ...assignedUser, name });
        return { name, type, isExplicit: true };
      }
    }

    // Fallback: derive from last comment's user
    if (!request.comments || request.comments.length === 0) return undefined;
    const lastComment = request.comments[request.comments.length - 1];

    // Derive name
    // @ts-ignore - user.name exists but may not be in type
    const name = lastComment?.user?.name || lastComment?.userLocation?.username;

    // Email request type always shows ext-email avatar
    if (request.type === 'Email') {
      return { name, type: 'ext-email', isExplicit: false };
    }

    // Use deriveUserType for consistent type detection
    const userForType = {
      name: name || '',
      role: lastComment?.userLocation?.role,
      isHoneInternalUser: lastComment?.user?.isInternalUser || lastComment?.userLocation?.isHoneInternalUser,
      userId: lastComment?.userLocation?.userId || '',
    };
    const type = deriveUserType(userForType);

    return { name, type, isExplicit: false };
  };

  /** Check if request is internal (all comments have internal visibility) */
  const isInternal = (request: AskRequest): boolean => {
    if (!request.comments || request.comments.length === 0) return false;
    return request.comments.every((comment: any) => {
      const visibility = comment?.visibility?.toLowerCase();
      return visibility === 'internal';
    });
  };

  // Toggle handlers for toolbar filters
  const handleMentionFilterToggle = useCallback(() => {
    setMentionFilterActive(!mentionFilterActive);
  }, [mentionFilterActive, setMentionFilterActive]);

  const handleAttachmentFilterToggle = useCallback(() => {
    setAttachmentFilterActive(!attachmentFilterActive);
  }, [attachmentFilterActive, setAttachmentFilterActive]);

  const handleAssignedFilterToggle = useCallback(() => {
    setAssignedFilterActive(!assignedFilterActive);
  }, [assignedFilterActive, setAssignedFilterActive]);

  const handleGroupFilterToggle = useCallback(() => {
    setGroupFilterActive(!groupFilterActive);
  }, [groupFilterActive, setGroupFilterActive]);

  const handleWorkStateFilterChange = useCallback((states: Set<WorkState>) => {
    setWorkStateFilter(states);
  }, []);

  const handleDueDateFilterChange = useCallback((preset: DueDatePreset) => {
    setDueDateFilter(preset);
  }, []);

  const handleDateRangeFilterChange = useCallback((range: DateRange) => {
    setDateRangeFilter(range);
  }, []);

  const handleRequestTypeFilterChange = useCallback((types: Set<RequestTypeFilter>) => {
    setRequestTypeFilter(types);
  }, []);

  const handleAssigneeFilterChange = useCallback((userId: string | null) => {
    setAssigneeFilter(userId);
  }, []);

  const handleDateFieldChange = useCallback((field: DateField) => {
    setDateField(field);
  }, []);

  // Create request modal handlers
  const handleCreateTodo = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCreateEmail = useCallback(() => {
    setEmailModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCloseEmailModal = useCallback(() => {
    setEmailModalOpen(false);
  }, []);

  const handleCreateGroup = useCallback((includeChildren = false) => {
    setIncludeChildren(includeChildren);
    setGroupModalOpen(true);
  }, []);

  const handleGroupSubmit = useCallback(
    (data: { title: string }) => {
      if (currentLocationId) {
        const childRequestIds = includeChildren ? Array.from(selectedIds) : undefined;
        createGroup({ title: data.title, comment: 'Group created', childRequestIds });
      }
      setGroupModalOpen(false);
      deselectAll();
    },
    [currentLocationId, createGroup, selectedIds, includeChildren, deselectAll]
  );

  // Add to group handler
  const handleAddToGroup = useCallback(
    async (groupId: string) => {
      if (!currentLocationId) return;
      const requestIds = Array.from(selectedIds);

      await addMultipleToGroup(requestIds, groupId);

      // Clear selection after adding
      deselectAll();
    },
    [selectedIds, currentLocationId, addMultipleToGroup, deselectAll, queryRequests]
  );

  // Remove from group handler
  const handleRemoveFromGroup = useCallback(async () => {
    if (!currentLocationId) return;
    const requestIds = Array.from(selectedIds);

    await removeMultipleFromGroup(requestIds, activeGroupId || undefined);

    // Clear selection after removing
    deselectAll();
  }, [selectedIds, currentLocationId, removeMultipleFromGroup, deselectAll, activeGroupId]);

  return (
    <div className="ksq2-browser-panel">
      <BrowserToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onCreateTodo={handleCreateTodo}
        onCreateEmail={handleCreateEmail}
        onCreateGroup={() => handleCreateGroup(false)}
        sortAscending={sortAscending}
        onSortToggle={toggleSortOrder}
        dateField={dateField}
        onDateFieldChange={handleDateFieldChange}
        mentionFilterActive={mentionFilterActive}
        attachmentFilterActive={attachmentFilterActive}
        assignedFilterActive={assignedFilterActive}
        workStateFilter={workStateFilter}
        dueDateFilter={dueDateFilter}
        dateRangeFilter={dateRangeFilter}
        requestTypeFilter={requestTypeFilter}
        onMentionFilterToggle={handleMentionFilterToggle}
        onAttachmentFilterToggle={handleAttachmentFilterToggle}
        onAssignedFilterToggle={handleAssignedFilterToggle}
        onWorkStateFilterChange={handleWorkStateFilterChange}
        onDueDateFilterChange={handleDueDateFilterChange}
        onDateRangeFilterChange={handleDateRangeFilterChange}
        onRequestTypeFilterChange={handleRequestTypeFilterChange}
        mentionCount={filterCounts.mentionCount}
        attachmentCount={filterCounts.attachmentCount}
        assignedCount={filterCounts.assignedCount}
        workStateCounts={filterCounts.workStateCounts}
        requestTypeCounts={filterCounts.requestTypeCounts}
        assigneeFilter={assigneeFilter}
        onAssigneeFilterChange={handleAssigneeFilterChange}
        assigneeOptions={assigneeOptions}
        activeView={activeView}
        groupCount={filterCounts.groupCount}
        groupFilterActive={groupFilterActive}
        onGroupFilterToggle={handleGroupFilterToggle}
      />
      <div className="ksq2-browser-panel__list-wrapper">
        <MultiSelectBar
          selectedCount={selectedIds.size}
          totalCount={allVisibleRequests.length}
          isVisible={selectedIds.size > 1}
          activeView={activeView}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onAssign={handleBulkAssign}
          onSetDueDate={handleBulkSetDueDate}
          onResolve={handleBulkResolve}
          onReopen={handleBulkReopen}
          onToggleBlocker={handleBulkToggleBlocker}
          onRemoveToggleBlock={handleRemoveToggleBlock}
          selectedAreBlockers={selectedAreBlockers}
          assigneeUsers={locationUsers}
          currentUser={currentUser}
          groups={groups}
          currentGroupId={activeGroupId}
          onCreateGroup={() => handleCreateGroup(true)}
          onAddToGroup={handleAddToGroup}
          onRemoveFromGroup={handleRemoveFromGroup}
        />
        <div
          ref={listRef}
          className="ksq2-browser-panel__list"
          role="listbox"
          aria-multiselectable="true"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {/* Loading skeleton - shows expected number of items based on view count */}
          {isQueryLoading && <SkeletonLoading count={activeViewCount || 5} />}
          {/* Blockers section - shown at top of Me/Team/Client views */}
          {!isQueryLoading && filteredViewBlockers.length > 0 && (
            <div className="ksq2-browser-panel__group ksq2-browser-panel__group--blockers">
              <GroupHeader
                label="Blockers"
                count={filteredViewBlockers.length}
                isExpanded={!blockersCollapsed}
                isFocused={focusedGroupKey === 'blockers'}
                groupKey="blockers"
                onToggle={toggleBlockersSection}
              />
              {!blockersCollapsed && (
                <div className="ksq2-browser-panel__group-items">
                  {filteredViewBlockers.map((request: AskRequest) => {
                    const fieldDate = getDateForField(request, dateField);
                    const displayDate = fieldDate || getLastActivityDate(request);

                    // Render CollapsibleGroupCard for Group type requests (unlikely in blockers but for consistency)
                    if (request.type === 'Group') {
                      return (
                        <CollapsibleGroupCard
                          key={request.id}
                          group={request}
                          allRequests={queryRequests}
                          activeView={activeView}
                          userId={userId}
                          isFocused={focusedId === request.id}
                          isSelected={selectedIds.has(request.id)}
                          isMultiSelectMode={isMultiSelectMode}
                          selectedIds={selectedIds}
                          focusedId={focusedId}
                          filterRequests={filterRequests}
                          dateField={dateField}
                          groupKey="thisMonth"
                          formatCardDateLabel={formatCardDateLabel}
                          formatFullDateTime={formatFullDateTime}
                          onClick={e => handleCardClick(request.id, e)}
                          onChildClick={handleCardClick}
                          onChildCheckChange={handleCheckChange}
                          requestCardUsers={requestCardUsers}
                          checkHasMention={checkHasMention}
                          checkHasAttachment={checkHasAttachment}
                          checkGetWorkState={checkGetWorkState}
                          getAssignee={getAssignee}
                          isInternal={isInternal}
                          highlightQuery={highlightQuery}
                          getDateForField={getDateForField}
                          getLastActivityDate={getLastActivityDate}
                          dateFieldLabels={dateFieldLabels}
                          parseLocalDate={parseLocalDate}
                          getShortId={getShortId}
                          dragDisabled={activeView === 'Completed'}
                          totalGroupChildCount={groups.find(g => g.id === request.id)?.requestCount}
                        />
                      );
                    }

                    return (
                      <DraggableRequestCard
                        key={request.id}
                        id={request.id}
                        title={request.title || request.identifier || `Request #${getShortId(request.id)}`}
                        date={fieldDate ? formatCardDateLabel(displayDate, 'thisMonth') : '—'}
                        dateTooltip={fieldDate ? formatFullDateTime(displayDate) : `No ${dateFieldLabels[dateField]}`}
                        dueDate={request.dueDate ? parseLocalDate(request.dueDate) : undefined}
                        assignee={getAssignee(request)}
                        requesterName={request.comments?.[0]?.userLocation?.email?.split('@')[0]}
                        memo={request.comments?.[request.comments.length - 1]?.comment}
                        users={requestCardUsers}
                        // @ts-ignore - flaggedAmount and transaction.amount may exist
                        amount={request.flaggedAmount ? parseFloat(request.flaggedAmount) : request.transaction?.amount}
                        workState={checkGetWorkState(request)}
                        hasMention={checkHasMention(request)}
                        hasAttachment={checkHasAttachment(request)}
                        isInternal={isInternal(request)}
                        isFocused={focusedId === request.id}
                        isSelected={selectedIds.has(request.id)}
                        isMultiSelectMode={isMultiSelectMode}
                        searchQuery={highlightQuery}
                        selectedIds={Array.from(selectedIds)}
                        dragDisabled={activeView === 'Completed'}
                        onClick={e => handleCardClick(request.id, e)}
                        onCheckChange={checked => handleCheckChange(request.id, checked)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!isQueryLoading &&
            dateGroups.map(group => (
              <div key={group.key} className="ksq2-browser-panel__group">
                <GroupHeader
                  label={group.label}
                  count={group.requests.length}
                  isExpanded={!collapsedGroups.has(group.key)}
                  isFocused={focusedGroupKey === group.key}
                  groupKey={group.key}
                  onToggle={() => {
                    toggleGroup(group.key);
                    focusGroup(group.key);
                  }}
                  onDoubleClick={toggleAllGroups}
                />
                {!collapsedGroups.has(group.key) && (
                  <div className="ksq2-browser-panel__group-items">
                    {group.requests.map((request: AskRequest) => {
                      // Use selected date field, fallback to last activity for display
                      const fieldDate = getDateForField(request, dateField);
                      const displayDate = fieldDate || getLastActivityDate(request);

                      // Render CollapsibleGroupCard for Group type requests
                      if (request.type === 'Group') {
                        return (
                          <CollapsibleGroupCard
                            key={request.id}
                            group={request}
                            allRequests={queryRequests}
                            activeView={activeView}
                            userId={userId}
                            isFocused={focusedId === request.id}
                            isSelected={selectedIds.has(request.id)}
                            isMultiSelectMode={isMultiSelectMode}
                            selectedIds={selectedIds}
                            focusedId={focusedId}
                            filterRequests={filterRequests}
                            dateField={dateField}
                            groupKey={group.key}
                            formatCardDateLabel={formatCardDateLabel}
                            formatFullDateTime={formatFullDateTime}
                            onClick={e => handleCardClick(request.id, e)}
                            onChildClick={handleCardClick}
                            onChildCheckChange={handleCheckChange}
                            requestCardUsers={requestCardUsers}
                            checkHasMention={checkHasMention}
                            checkHasAttachment={checkHasAttachment}
                            checkGetWorkState={checkGetWorkState}
                            getAssignee={getAssignee}
                            isInternal={isInternal}
                            highlightQuery={highlightQuery}
                            getDateForField={getDateForField}
                            getLastActivityDate={getLastActivityDate}
                            dateFieldLabels={dateFieldLabels}
                            parseLocalDate={parseLocalDate}
                            getShortId={getShortId}
                            dragDisabled={activeView === 'Completed'}
                            totalGroupChildCount={groups.find(g => g.id === request.id)?.requestCount}
                          />
                        );
                      }

                      return (
                        <DraggableRequestCard
                          key={request.id}
                          id={request.id}
                          title={request.title || request.identifier || `Request #${getShortId(request.id)}`}
                          date={fieldDate ? formatCardDateLabel(displayDate, group.key) : '—'}
                          dateTooltip={fieldDate ? formatFullDateTime(displayDate) : `No ${dateFieldLabels[dateField]}`}
                          dueDate={request.dueDate ? parseLocalDate(request.dueDate) : undefined}
                          assignee={getAssignee(request)}
                          requesterName={request.comments?.[0]?.userLocation?.email?.split('@')[0]}
                          memo={request.comments?.[request.comments.length - 1]?.comment}
                          users={requestCardUsers}
                          // @ts-ignore - flaggedAmount and transaction.amount may exist
                          amount={
                            request.flaggedAmount
                              ? parseFloat(String(request.flaggedAmount))
                              : request.transaction?.amount
                          }
                          workState={checkGetWorkState(request)}
                          hasMention={checkHasMention(request)}
                          hasAttachment={checkHasAttachment(request)}
                          isInternal={isInternal(request)}
                          isFocused={focusedId === request.id}
                          isSelected={selectedIds.has(request.id)}
                          isMultiSelectMode={isMultiSelectMode}
                          searchQuery={highlightQuery}
                          selectedIds={Array.from(selectedIds)}
                          dragDisabled={activeView === 'Completed'}
                          onClick={e => handleCardClick(request.id, e)}
                          onCheckChange={checked => handleCheckChange(request.id, checked)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          {!isQueryLoading &&
            dateGroups.length === 0 &&
            (() => {
              // Determine why the list is empty
              const hasToolbarFiltersActive =
                searchQuery.trim() ||
                mentionFilterActive ||
                attachmentFilterActive ||
                assignedFilterActive ||
                workStateFilter.size > 0 ||
                dueDateFilter !== null ||
                dateRangeFilter.from !== null ||
                dateRangeFilter.to !== null ||
                requestTypeFilter.size > 0 ||
                assigneeFilter !== null;

              const isFilteredOut = viewFilteredRequests.length > 0 && hasToolbarFiltersActive;

              if (isFilteredOut) {
                // Requests exist but toolbar filters/search excluded them
                return (
                  <EmptyState
                    icon={<IconFilter />}
                    title="No matching To-Do's"
                    description="Try adjusting your search or filters"
                    variant="subtle"
                    action={{ label: 'Clear filters', onClick: resetFilters, icon: <IconCancel /> }}
                  />
                );
              } else {
                // No requests in this primary filter view at all
                return (
                  <EmptyState
                    icon={<IconTaskNew />}
                    title="No To-Do's yet"
                    description="Create a new To-Do to get started"
                    variant="subtle"
                    action={{ label: 'Create To-Do', onClick: handleCreateTodo, icon: <IconAddNew /> }}
                  />
                );
              }
            })()}
        </div>
        <ResolveDialog
          isOpen={isBulkResolveOpen}
          isPending={patchMutation.isPending}
          onClose={() => setIsBulkResolveOpen(false)}
          onSubmit={handleBulkResolveSubmit}
          title="Resolve Requests"
          commentLabel="Closing comment (optional)"
          commentPlaceholder="Add a closing comment to explain the resolution."
          submitLabel="Resolve All"
        />
        <ReopenDialog
          isOpen={isBulkReopenOpen}
          isPending={patchMutation.isPending}
          onClose={() => {
            setIsBulkReopenOpen(false);
            setBulkReopenInfo(null);
          }}
          onSubmit={handleBulkReopenSubmit}
          title="Reopen Requests"
          commentLabel="Reopen comment (optional)"
          commentPlaceholder="Add a note explaining why these requests are being reopened."
          submitLabel="Reopen All"
          infoMessage={bulkReopenInfo ?? undefined}
        />
      </div>
      <QueueCreateAskReq isOpen={createModalOpen} handleClose={handleCloseCreateModal} />
      <CreateRequestEmailModal
        isOpen={emailModalOpen}
        handleClose={handleCloseEmailModal}
        resetState={handleCloseEmailModal}
        locationName={locationName}
        locationId={currentLocationId}
      />
      <CreateGroupModal
        isOpen={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        onSubmit={handleGroupSubmit}
        locationName={locationName}
      />
    </div>
  );
};
